"use client";

import { useEffect, useState } from "react";

type MarginRow = {
  symbol: string;
  var_value: number;
  hair_cut: number;
  acc_qty_pct: number;
  half_hour_avg: number;
  free_float: number;
  date: string;
};

type SummaryData = {
  generated_at: string;
  as_of?: { margins?: string };
  margins: {
    top_var: MarginRow[];
    top_haircut: MarginRow[];
    top_acc_qty: MarginRow[];
  };
  history?: {
    margins?: {
      date: string;
      avg_var: number;
      avg_haircut: number;
      max_var: number;
      max_haircut: number;
      symbols: number;
    }[];
  };
  intelligence?: {
    margins?: {
      summary?: string;
      avg_var?: number;
      avg_haircut?: number;
      avg_var_change_label?: string;
      avg_haircut_change_label?: string;
    };
  };
  insights: {
    margins?: { headline?: string; haircut?: string };
  };
};

export default function MarginsPage() {
  const [data, setData] = useState<SummaryData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setError(null);
      try {
        const res = await fetch("/api/outputs/mfs_mts_summary.json");
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
      }
    };
    load();
  }, []);

  const renderCard = (title: string, value?: string) => (
    <div className="strategy-detail-card" style={{ padding: "0.9rem 1rem" }}>
      <div style={{ color: "var(--color-text-muted)", fontSize: "0.85rem" }}>{title}</div>
      <div style={{ color: "var(--color-text)", fontWeight: 600 }}>{value || "—"}</div>
    </div>
  );

  const renderTable = (headers: string[], rows: (string | number)[][]) => (
    <div style={{ overflowX: "auto" }}>
      <table className="results-table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => (
            <tr key={`${r[0]}-${idx}`}>
              {r.map((cell, i) => (
                <td key={`${idx}-${i}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      {error && <p style={{ color: "var(--color-danger)" }}>Error: {error}</p>}
      {!data && !error && <p>Loading funding data...</p>}

      {data && (
        <>
          <p style={{ marginBottom: "1rem", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
            All values shown in percentages unless stated otherwise.
          </p>

          <div style={{ marginBottom: "1.5rem", display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            {renderCard("Highest VAR", data.insights?.margins?.headline)}
            {renderCard("Highest Haircut", data.insights?.margins?.haircut)}
          </div>

          <h3 style={{ margin: "0 0 0.5rem" }}>Highest VAR %</h3>
          {renderTable(
            ["Symbol", "VAR %", "Haircut %", "Acc Qty %"],
            data.margins.top_var.map((r) => [
              r.symbol,
              r.var_value.toFixed(2),
              r.hair_cut.toFixed(2),
              r.acc_qty_pct.toFixed(2),
            ])
          )}
          <div style={{ height: "1.5rem" }} />
          <h3 style={{ margin: "0 0 0.5rem" }}>Highest Haircut %</h3>
          {renderTable(
            ["Symbol", "Haircut %", "VAR %", "Acc Qty %"],
            data.margins.top_haircut.map((r) => [
              r.symbol,
              r.hair_cut.toFixed(2),
              r.var_value.toFixed(2),
              r.acc_qty_pct.toFixed(2),
            ])
          )}
          <div style={{ height: "1.5rem" }} />
          <h3 style={{ margin: "0 0 0.5rem" }}>Highest Acc Qty %</h3>
          {renderTable(
            ["Symbol", "Acc Qty %", "VAR %", "Haircut %"],
            data.margins.top_acc_qty.map((r) => [
              r.symbol,
              r.acc_qty_pct.toFixed(2),
              r.var_value.toFixed(2),
              r.hair_cut.toFixed(2),
            ])
          )}
        </>
      )}
    </>
  );
}
